import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { BaselineDialogProps, FormType } from "../../common";
import { StageType } from "../../../../../../graphql/types";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { beefTabsStyles } from "../../../../../../_metronic/layout";
import { DairyV2Emissions, DairyV2Intervention } from "../../../../models/Intervention/DairyV2Intervention";
import { useFormContext } from "react-hook-form";
import { BeefBaseline, BeefBreedingStageData, BeefGrowingStageData } from "../../../../models/Baseline/BeefBaseline";
import { cloneDeep } from "lodash";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import { Box, Tab, Tabs } from "@material-ui/core";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import BeefInterventionEmissionMitigationForm from "./BeefInterventionEmissionMitigationForm";
import { CattleEmission, TargetAnimal } from "../../../../models/Intervention/CattleIntervention";

interface TabPanelProps {
  children: ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`beef-breeding-emissions-${index}`}
      aria-labelledby={`beef-breeding-emissions-${index}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

interface BeefInterventionEmissionMitigationDialogProp extends BaselineDialogProps {
  stageType: StageType,
  formType: FormType;
  stageIndex: number;
  baseline: BeefBaseline;
  handleCancel: (param?: string | null) => void;
}

const BeefInterventionEmissionMitigationDialog: FC<BeefInterventionEmissionMitigationDialogProp> = ({
  stageType,
  formType = FormType.Add,
  stageIndex = 0,
  baseline,
  handleCancel,
}) => {
  const intl = useIntl();
  const classes = beefTabsStyles();
  const formTitle = intl.formatMessage({ id: 'SUSTELL.PROCESS.DIALOG.STAGE.EMISSIONS.TITLE', });
  const introText = intl.formatMessage({ id: 'EMISSIONS.INTRO', });

  const currResetValue = useRef<DairyV2Emissions>();
  const fc = useFormContext<DairyV2Intervention>();

  const [isEmissionEdit, setIsEmissionEdit] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState(0);


  const fieldItemPrefix = `stages.${stageIndex}.stageData.emissions`;
  const currentStageData = baseline.stages[stageIndex].stageData;

  const getEmissionsFromStageType = (currentStageData: any) => {
    if (stageType === StageType.Growing)
      return (currentStageData as BeefGrowingStageData).emissions;
    return (currentStageData as BeefBreedingStageData).emissions;
  }

  const handleTabChange = (event: React.ChangeEvent<any>, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleDialogCancelClick = () => {
    if (currResetValue.current) {
      for (let [propertyName, key] of Object.entries(currResetValue.current)) {
        fc.setValue(`${fieldItemPrefix}.${propertyName}`, key);
      }
    }
    if (handleCancel) {
      handleCancel();
    }
  };

  const beefEmissionsData = getEmissionsFromStageType(currentStageData) ?? {};

  const resetEdit = () => {
    for (let [propertyName] of Object.entries(beefEmissionsData)) {
      fc.setValue(`${fieldItemPrefix}.${propertyName}`, "");
    }
    setIsEmissionEdit(false);
  };

  useEffect(() => {
    currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
  });

  const getEmissionData = (target: TargetAnimal) => {
    if (target === TargetAnimal.COWS)
      return (beefEmissionsData as { cows?: CattleEmission }).cows ?? {};

    return (beefEmissionsData as { calves?: CattleEmission }).calves ?? {};
  }


  return (
    <DialogContainer
      formVisible={true}
      handleClose={handleDialogCancelClick}
      iconCode="maps-travel/globe-04"
      formTitle={formTitle}
      introText={introText}
      datasetType="intervention"
      width={isEmissionEdit ? "1288px" : "936px"}
      dialogContentStyles={{
        paddingBottom: '1.25rem',
        overflowY: 'unset',
      }}
    >
      {stageType === StageType.Breeding ? (
        <>
          <Box className={classes.tabsBox}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="beef-breeding-emissions"
              className={classes.tabs}
            >
              <Tab
                label={intl.formatMessage({
                  id: `SUSTELL.STAGE.BEEF.STEPS.COWS`,
                })}
              />
              <Tab
                label={intl.formatMessage({
                  id: `SUSTELL.STAGE.BEEF.STEPS.CALVES`,
                })}
              />
            </Tabs>
          </Box>
          <TabPanel value={currentTab} index={0}>
            <BeefInterventionEmissionMitigationForm
              formType={formType}
              fieldItemPrefix={`${fieldItemPrefix}.${TargetAnimal.COWS}`}
              beefEmissionsData={getEmissionData(TargetAnimal.COWS)}
              isEmissionEdit={isEmissionEdit}
              setIsEmissionEdit={setIsEmissionEdit}
              resetEdit={resetEdit}
            />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <BeefInterventionEmissionMitigationForm
              formType={formType}
              fieldItemPrefix={`${fieldItemPrefix}.${TargetAnimal.CALVES}`}
              beefEmissionsData={getEmissionData(TargetAnimal.CALVES)}
              isEmissionEdit={isEmissionEdit}
              setIsEmissionEdit={setIsEmissionEdit}
              resetEdit={resetEdit}
            />
          </TabPanel>
        </>
      ) : (
        <BeefInterventionEmissionMitigationForm
          formType={formType}
          fieldItemPrefix={fieldItemPrefix}
          beefEmissionsData={beefEmissionsData as CattleEmission}
          isEmissionEdit={isEmissionEdit}
          setIsEmissionEdit={setIsEmissionEdit}
          resetEdit={resetEdit}
        />
      )}

      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
      />

    </DialogContainer>
  )
}

export default BeefInterventionEmissionMitigationDialog;
